/**
 * Health API hooks. One file per app to keep imports tidy.
 *
 * All endpoints under /api/apps/health/* per ADR 0029.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend, ApiError } from './client';

/** Helper: treat 404 as "no record" → null. Other errors still throw. */
async function getOr404Null<T>(path: string): Promise<T | null> {
  try {
    return await apiGet<T>(path);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

export interface HealthProfileDto {
  id: string;
  userId: string;
  dob: string | null;
  heightCm: number | null;
  weightKg: number | null;
  restingHr: number | null;
  vo2Estimate: number | null;
  bodyFatPct: number | null;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export type HealthProfileInput = Partial<
  Pick<
    HealthProfileDto,
    'dob' | 'heightCm' | 'weightKg' | 'restingHr' | 'vo2Estimate' | 'bodyFatPct' | 'notes'
  >
>;

export function useHealthProfile() {
  return useQuery({
    queryKey: ['health', 'profile'],
    queryFn: async () => {
      const r = await getOr404Null<{ profile: HealthProfileDto }>('/apps/health/profile');
      return r?.profile ?? null;
    },
    staleTime: 60_000,
  });
}

export function useUpsertHealthProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: HealthProfileInput) =>
      apiSend<{ profile: HealthProfileDto }>('PUT', '/apps/health/profile', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['health', 'profile'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Goals
// ---------------------------------------------------------------------------

export type HealthGoalType =
  | 'metric_target'
  | 'workout_volume'
  | 'new_capability'
  | 'performance'
  | 'habit';

export type HealthGoalStatus =
  | 'on-track'
  | 'needs-attention'
  | 'at-risk'
  | 'achieved'
  | 'abandoned'
  | 'archived';

export interface HealthGoalDto {
  id: string;
  healthProfileId: string;
  type: HealthGoalType;
  title: string;
  metric: string | null;
  unit: string | null;
  baseline: unknown;
  baselineDate: string | null;
  target: unknown;
  targetDate: string | null;
  current: unknown;
  currentDate: string | null;
  phaseId: string | null;
  status: HealthGoalStatus;
  notes: string;
  history: Array<{ date: string; v?: unknown }>;
  progressSteps: Array<{ step: string; done: boolean }>;
  createdAt: string;
  updatedAt: string;
}

export function useHealthGoals() {
  return useQuery({
    queryKey: ['health', 'goals'],
    queryFn: () => apiGet<{ goals: HealthGoalDto[] }>('/apps/health/goals'),
    staleTime: 30_000,
  });
}

export function useCreateHealthGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<HealthGoalDto> & { type: HealthGoalType; title: string }) =>
      apiSend<{ goal: HealthGoalDto }>('POST', '/apps/health/goals', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'goals'] }),
  });
}

export function usePatchHealthGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Partial<HealthGoalDto> }) =>
      apiSend<{ goal: HealthGoalDto }>('PATCH', `/apps/health/goals/${vars.id}`, vars.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'goals'] }),
  });
}

// ---------------------------------------------------------------------------
// Check-ins
// ---------------------------------------------------------------------------

export interface HealthCheckinDto {
  id: string;
  date: string;
  energy: number | null;
  mood: number | null;
  soreness: number | null;
  stress: number | null;
  sleepQuality: number | null;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export function useHealthCheckins() {
  return useQuery({
    queryKey: ['health', 'checkins'],
    queryFn: () => apiGet<{ checkins: HealthCheckinDto[] }>('/apps/health/checkins'),
    staleTime: 30_000,
  });
}

export function useTodayCheckin() {
  return useQuery({
    queryKey: ['health', 'checkin', 'today'],
    queryFn: async () => {
      const r = await getOr404Null<{ checkin: HealthCheckinDto }>(
        '/apps/health/checkins/today',
      );
      return r?.checkin ?? null;
    },
    staleTime: 30_000,
  });
}

export function useUpsertCheckin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { date: string; patch: Partial<HealthCheckinDto> }) =>
      apiSend<{ checkin: HealthCheckinDto }>(
        'PUT',
        `/apps/health/checkins/${vars.date}`,
        vars.patch,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['health', 'checkins'] });
      qc.invalidateQueries({ queryKey: ['health', 'checkin', 'today'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Measurements
// ---------------------------------------------------------------------------

export interface MeasurementDto {
  id: string;
  type: string;
  value: number;
  unit: string | null;
  source: string;
  notes: string;
  recordedAt: string;
  createdAt: string;
}

export function useMeasurements(type?: string) {
  return useQuery({
    queryKey: ['health', 'measurements', { type }],
    queryFn: () =>
      apiGet<{ measurements: MeasurementDto[] }>(
        type
          ? `/apps/health/measurements?type=${encodeURIComponent(type)}`
          : '/apps/health/measurements',
      ),
    staleTime: 30_000,
  });
}

export function useCreateMeasurement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      type: string;
      value: number;
      unit?: string | null;
      source?: string;
      notes?: string;
      recordedAt?: string;
    }) =>
      apiSend<{ measurement: MeasurementDto }>('POST', '/apps/health/measurements', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'measurements'] }),
  });
}

// ---------------------------------------------------------------------------
// Sleep + recovery
// ---------------------------------------------------------------------------

export interface SleepSessionDto {
  id: string;
  date: string;
  totalH: number | null;
  deepH: number | null;
  remH: number | null;
  lightH: number | null;
  interruptions: number | null;
  efficiency: number | null;
  bedtime: string | null;
  wakeTime: string | null;
  score: number | null;
  source: string;
  notes: string;
  createdAt: string;
}

export interface RecoverySignalDto {
  id: string;
  date: string;
  hrv: number | null;
  soreness: number | null;
  mood: number | null;
  prevLoadScore: number | null;
  readinessScore: number | null;
  inputs: Record<string, unknown>;
  modelVersion: string | null;
  computedAt: string;
}

export function useSleepSessions() {
  return useQuery({
    queryKey: ['health', 'sleep'],
    queryFn: () => apiGet<{ sessions: SleepSessionDto[] }>('/apps/health/sleep'),
    staleTime: 30_000,
  });
}

export function useRecoverySignals() {
  return useQuery({
    queryKey: ['health', 'recovery'],
    queryFn: () => apiGet<{ signals: RecoverySignalDto[] }>('/apps/health/recovery'),
    staleTime: 30_000,
  });
}

export function useUpsertSleep() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { date: string; patch: Partial<SleepSessionDto> }) =>
      apiSend<{ session: SleepSessionDto }>(
        'PUT',
        `/apps/health/sleep/${vars.date}`,
        vars.patch,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'sleep'] }),
  });
}

// ---------------------------------------------------------------------------
// Training (phases + workouts + sets + exercises + templates)
// ---------------------------------------------------------------------------

export type ExerciseType =
  | 'strength'
  | 'cardio_steady'
  | 'cardio_intervals'
  | 'mobility'
  | 'plyometric'
  | 'hold';

export type ExerciseBlock = 'warmup' | 'main' | 'accessory' | 'cooldown';

export interface ExerciseDto {
  id: string;
  slug: string;
  name: string;
  type: ExerciseType;
  defaultBlock: ExerciseBlock;
  muscles: string[];
  equipment: string[];
  equipmentId: string | null;
  primaryMuscle: string | null;
  targetMuscles: string[];
  level: 'beginner' | 'intermediate' | 'advanced' | null;
  coverImageUrl: string | null;
  frontImageUrl: string | null;
  backImageUrl: string | null;
  cues: string[];
  prescription: string | null;
  referenceUrl: string | null;
  defaults: Record<string, unknown>;
  archivedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface WeeklyPattern {
  id: string;
  label: string;
  days: Array<{ day: string; session: string }>;
}

export interface PhaseAdjustment {
  date: string;
  note: string;
}

export interface TrainingPhaseDto {
  id: string;
  name: string;
  intent: string;
  primaryGoal: string | null;
  secondaryGoal: string | null;
  startDate: string | null;
  endDate: string | null;
  weeks: number | null;
  currentWeek: number;
  status: 'active' | 'completed' | 'abandoned' | 'archived';
  notes: string;
  assessmentOutcome: string | null;
  weeklyPatterns: WeeklyPattern[];
  weekAssignment: string[];
  adjustments: PhaseAdjustment[];
  linkedGoalIds: string[];
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface WorkoutSessionDto {
  id: string;
  phaseId: string | null;
  templateId: string | null;
  date: string;
  name: string;
  type: string;
  status: 'planned' | 'in_progress' | 'completed' | 'skipped';
  startTime: string | null;
  endTime: string | null;
  /** Set while paused (status stays in_progress); null while running. */
  pausedAt: string | null;
  /** Accumulated paused seconds from prior pause/resume cycles. */
  pausedTotalS: number;
  durationMin: number | null;
  difficultyAvg: number | null;
  rpe: number | null;
  notes: string;
  createdAt: string;
}

export interface ExerciseSetDto {
  id: string;
  workoutSessionId: string;
  exerciseName: string;
  exerciseId: string | null;
  block: string | null;
  setIndex: number;
  prescription: string | null;
  weight: number | null;
  reps: number | null;
  durationS: number | null;
  distanceM: number | null;
  difficulty: number | null;
  prevWeight: number | null;
  prevReps: number | null;
  prevDate: string | null;
  prevDifficulty: number | null;
  prevActual: string | null;
  actual: string | null;
  skipReason: string | null;
  status: 'planned' | 'completed' | 'skipped';
  notes: string;
  createdAt: string;
}

export interface TemplateExerciseDto {
  id: string;
  exerciseId: string;
  block: ExerciseBlock;
  position: number;
  prescription: string | null;
  defaultSets: number | null;
  notes: string;
  exercise: {
    id: string;
    slug: string;
    name: string;
    type: ExerciseType;
    muscles: string[];
    cues: string[];
    referenceUrl: string | null;
  };
}

export interface WorkoutTemplateDto {
  id: string;
  userId: string | null;
  name: string;
  description: string;
  type: string;
  durationMin: number | null;
  agentBuilt: boolean;
  usedCount: number;
  archivedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  exercises: TemplateExerciseDto[];
}

// ─── Phases ─────────────────────────────────────────────────────────────

export function useTrainingPhases() {
  return useQuery({
    queryKey: ['health', 'phases'],
    queryFn: () => apiGet<{ phases: TrainingPhaseDto[] }>('/apps/health/phases'),
    staleTime: 60_000,
  });
}

export function usePhase(id: string | null | undefined) {
  return useQuery({
    queryKey: ['health', 'phase', id],
    enabled: !!id,
    queryFn: () => apiGet<{ phase: TrainingPhaseDto }>(`/apps/health/phases/${id}`),
    staleTime: 60_000,
  });
}

export function useCreatePhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<TrainingPhaseDto> & { name: string }) =>
      apiSend<{ phase: TrainingPhaseDto }>('POST', '/apps/health/phases', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['health', 'phases'] });
      // Generation creates planned sessions; refresh the workouts list.
      qc.invalidateQueries({ queryKey: ['health', 'workouts'] });
    },
  });
}

export function usePatchPhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Partial<TrainingPhaseDto> }) =>
      apiSend<{ phase: TrainingPhaseDto }>('PATCH', `/apps/health/phases/${vars.id}`, vars.patch),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['health', 'phases'] });
      qc.invalidateQueries({ queryKey: ['health', 'phase', vars.id] });
      // Reconciliation may have created/deleted planned workouts.
      qc.invalidateQueries({ queryKey: ['health', 'workouts'] });
    },
  });
}

export function useDeletePhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiSend<{ ok: true }>('DELETE', `/apps/health/phases/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['health', 'phases'] });
      // Sessions have phase_id set null via FK; refresh listings.
      qc.invalidateQueries({ queryKey: ['health', 'workouts'] });
    },
  });
}

// ─── Workout sessions + sets ────────────────────────────────────────────

export function useWorkouts() {
  return useQuery({
    queryKey: ['health', 'workouts'],
    queryFn: () => apiGet<{ workouts: WorkoutSessionDto[] }>('/apps/health/workouts'),
    staleTime: 30_000,
  });
}

/**
 * Active (in-progress) workout sessions — powers the sidebar "Active
 * workout" nav item, the Health Today resume banner, and the Workouts
 * chooser. Short staleTime + refetch-on-focus so the badge/banner clear
 * promptly once a session is completed elsewhere (e.g. another tab).
 */
export function useActiveWorkouts(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['health', 'workouts', 'active'],
    queryFn: () =>
      apiGet<{ workouts: WorkoutSessionDto[] }>('/apps/health/workouts?status=in_progress'),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

export function useWorkoutSession(id: string | null | undefined) {
  return useQuery({
    queryKey: ['health', 'workout', id],
    enabled: !!id,
    queryFn: () =>
      apiGet<{ workout: WorkoutSessionDto; sets: ExerciseSetDto[] }>(
        `/apps/health/workouts/${id}`,
      ),
    staleTime: 10_000,
  });
}

export function useCreateWorkout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<WorkoutSessionDto> & { date: string; name: string }) =>
      apiSend<{ workout: WorkoutSessionDto }>('POST', '/apps/health/workouts', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'workouts'] }),
  });
}

export function usePatchWorkout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Partial<WorkoutSessionDto> }) =>
      apiSend<{ workout: WorkoutSessionDto }>(
        'PATCH',
        `/apps/health/workouts/${vars.id}`,
        vars.patch,
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['health', 'workouts'] });
      qc.invalidateQueries({ queryKey: ['health', 'workout', vars.id] });
    },
  });
}

/**
 * Pause / resume an in-progress workout. Paused is not a status — the
 * session stays in_progress with pausedAt set (see health-workout-pause
 * design). Invalidate the same keys usePatchWorkout does, including the
 * ['health','workouts'] prefix so useActiveWorkouts (['health','workouts',
 * 'active']) refreshes and the sidebar/banner/chooser re-render.
 */
export function usePauseWorkout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ workout: WorkoutSessionDto }>('POST', `/apps/health/workouts/${id}/pause`, {}),
    // Invalidate on SETTLED (success or error) so a failed transition — e.g. a
    // double-click 409 or a stale-tab race — still refetches the authoritative
    // state and the UI self-corrects. The tracker surfaces isError to explain
    // the auto-refresh; see WorkoutTracker.tsx.
    onSettled: (_data, _err, id) => {
      qc.invalidateQueries({ queryKey: ['health', 'workouts'] });
      qc.invalidateQueries({ queryKey: ['health', 'workout', id] });
    },
  });
}

export function useResumeWorkout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ workout: WorkoutSessionDto }>('POST', `/apps/health/workouts/${id}/resume`, {}),
    onSettled: (_data, _err, id) => {
      qc.invalidateQueries({ queryKey: ['health', 'workouts'] });
      qc.invalidateQueries({ queryKey: ['health', 'workout', id] });
    },
  });
}

export function useAddSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { sessionId: string; input: Partial<ExerciseSetDto> & { exerciseName: string } }) =>
      apiSend<{ set: ExerciseSetDto }>(
        'POST',
        `/apps/health/workouts/${vars.sessionId}/sets`,
        vars.input,
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['health', 'workout', vars.sessionId] });
    },
  });
}

export function usePatchSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { sessionId: string; setId: string; patch: Partial<ExerciseSetDto> }) =>
      apiSend<{ set: ExerciseSetDto }>(
        'PATCH',
        `/apps/health/workouts/${vars.sessionId}/sets/${vars.setId}`,
        vars.patch,
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['health', 'workout', vars.sessionId] });
    },
  });
}

export function useDeleteSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { sessionId: string; setId: string }) =>
      apiSend<{ ok: true }>(
        'DELETE',
        `/apps/health/workouts/${vars.sessionId}/sets/${vars.setId}`,
      ),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['health', 'workout', vars.sessionId] });
    },
  });
}

// ─── Exercise library ───────────────────────────────────────────────────

export function useExercises(
  options: {
    type?: ExerciseType;
    search?: string;
    muscles?: string[];
    equipmentIds?: string[];
  } = {},
) {
  const qs = new URLSearchParams();
  if (options.type) qs.set('type', options.type);
  if (options.search) qs.set('search', options.search);
  if (options.muscles?.length) qs.set('muscles', options.muscles.join(','));
  if (options.equipmentIds?.length) qs.set('equipmentIds', options.equipmentIds.join(','));
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return useQuery({
    queryKey: ['health', 'exercises', options],
    queryFn: () =>
      apiGet<{ exercises: ExerciseDto[] }>(`/apps/health/exercises${suffix}`),
    staleTime: 60_000,
  });
}

export function useCreateExercise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<ExerciseDto> & { name: string; type: ExerciseType }) =>
      apiSend<{ exercise: ExerciseDto }>('POST', '/apps/health/exercises', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'exercises'] }),
  });
}

export function usePatchExercise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Partial<ExerciseDto> }) =>
      apiSend<{ exercise: ExerciseDto }>(
        'PATCH',
        `/apps/health/exercises/${vars.id}`,
        vars.patch,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'exercises'] }),
  });
}

export function useArchiveExercise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ exercise: ExerciseDto }>('DELETE', `/apps/health/exercises/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'exercises'] }),
  });
}

// ─── Equipment library ───────────────────────────────────────────────────

export interface EquipmentDto {
  id: string;
  slug: string;
  name: string;
  iconKind: string;
  archivedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export function useEquipment() {
  return useQuery({
    queryKey: ['health', 'equipment'],
    queryFn: () => apiGet<{ equipment: EquipmentDto[] }>('/apps/health/equipment'),
    select: (r) => r.equipment,
    staleTime: 300_000,
  });
}

export function useCreateEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; iconKind?: string; slug?: string }) =>
      apiSend<{ equipment: EquipmentDto }>('POST', '/apps/health/equipment', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'equipment'] }),
  });
}

export function usePatchEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Partial<Pick<EquipmentDto, 'name' | 'iconKind'>> }) =>
      apiSend<{ equipment: EquipmentDto }>(
        'PATCH',
        `/apps/health/equipment/${vars.id}`,
        vars.patch,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'equipment'] }),
  });
}

export function useArchiveEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ equipment: EquipmentDto }>('DELETE', `/apps/health/equipment/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'equipment'] }),
  });
}

// ─── Exercise favorites ──────────────────────────────────────────────────

export function useFavoriteExerciseIds() {
  return useQuery({
    queryKey: ['health', 'exercise-favorites'],
    queryFn: () => apiGet<{ favoriteIds: string[] }>('/apps/health/exercises/favorites/ids'),
    select: (r) => r.favoriteIds,
    staleTime: 60_000,
  });
}

export function useToggleFavorite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; favored: boolean }) =>
      vars.favored
        ? apiSend<{ ok: true }>('DELETE', `/apps/health/exercises/${vars.id}/favorite`)
        : apiSend<{ ok: true }>('PUT', `/apps/health/exercises/${vars.id}/favorite`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'exercise-favorites'] }),
  });
}

// ─── Workout templates ──────────────────────────────────────────────────

export function useWorkoutTemplates(options: { type?: string } = {}) {
  const qs = options.type ? `?type=${encodeURIComponent(options.type)}` : '';
  return useQuery({
    queryKey: ['health', 'workout-templates', options],
    queryFn: () =>
      apiGet<{ templates: WorkoutTemplateDto[] }>(`/apps/health/workout-templates${qs}`),
    staleTime: 60_000,
  });
}

export function useWorkoutTemplate(id: string | null | undefined) {
  return useQuery({
    queryKey: ['health', 'workout-template', id],
    enabled: !!id,
    queryFn: () =>
      apiGet<{ template: WorkoutTemplateDto }>(`/apps/health/workout-templates/${id}`),
    staleTime: 30_000,
  });
}

export function useCreateWorkoutTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      description?: string;
      type?: string;
      durationMin?: number | null;
      exercises?: Array<{
        exerciseId: string;
        block?: ExerciseBlock;
        position?: number;
        prescription?: string | null;
        defaultSets?: number | null;
        notes?: string;
      }>;
    }) =>
      apiSend<{ template: WorkoutTemplateDto }>(
        'POST',
        '/apps/health/workout-templates',
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'workout-templates'] }),
  });
}

export function usePatchWorkoutTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: Partial<WorkoutTemplateDto> }) =>
      apiSend<{ template: WorkoutTemplateDto }>(
        'PATCH',
        `/apps/health/workout-templates/${vars.id}`,
        vars.patch,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'workout-templates'] }),
  });
}

export function useStartFromTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; date?: string; phaseId?: string | null }) =>
      apiSend<{ sessionId: string }>(
        'POST',
        `/apps/health/workout-templates/${vars.id}/start`,
        { date: vars.date, phaseId: vars.phaseId },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['health', 'workouts'] });
      qc.invalidateQueries({ queryKey: ['health', 'workout-templates'] });
    },
  });
}

export function useArchiveWorkoutTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiSend<{ template: WorkoutTemplateDto }>(
        'DELETE',
        `/apps/health/workout-templates/${id}`,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'workout-templates'] }),
  });
}

export function useReplaceTemplateExercises() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      id: string;
      exercises: Array<{
        exerciseId: string;
        block?: ExerciseBlock;
        position?: number;
        prescription?: string | null;
        defaultSets?: number | null;
        notes?: string;
      }>;
    }) =>
      apiSend<{ template: WorkoutTemplateDto }>(
        'PUT',
        `/apps/health/workout-templates/${vars.id}/exercises`,
        { exercises: vars.exercises },
      ),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['health', 'workout-templates'] });
      qc.invalidateQueries({ queryKey: ['health', 'workout-template', vars.id] });
    },
  });
}

// ---------------------------------------------------------------------------
// Nutrition
// ---------------------------------------------------------------------------

export interface NutritionTargetDto {
  id: string;
  kcal: number | null;
  proteinG: number | null;
  carbsG: number | null;
  fatG: number | null;
  fiberG: number | null;
  hydrationL: number | null;
  strategy: string;
  effectiveFrom: string;
  effectiveTo: string | null;
  createdAt: string;
}

export interface NutritionDayDto {
  id: string;
  date: string;
  kcal: number | null;
  proteinG: number | null;
  carbsG: number | null;
  fatG: number | null;
  fiberG: number | null;
  hydrationL: number | null;
  compliance: number | null;
  mealsLogged: number | null;
  source: string;
  notes: string;
  syncedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export function useNutritionTarget() {
  return useQuery({
    queryKey: ['health', 'nutrition', 'target'],
    queryFn: async () => {
      const r = await getOr404Null<{ target: NutritionTargetDto }>(
        '/apps/health/nutrition/target',
      );
      return r?.target ?? null;
    },
    staleTime: 60_000,
  });
}

export function useCreateNutritionTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<NutritionTargetDto>) =>
      apiSend<{ target: NutritionTargetDto }>(
        'POST',
        '/apps/health/nutrition/target',
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'nutrition'] }),
  });
}

export function useNutritionDays() {
  return useQuery({
    queryKey: ['health', 'nutrition', 'days'],
    queryFn: () => apiGet<{ days: NutritionDayDto[] }>('/apps/health/nutrition/days'),
    staleTime: 30_000,
  });
}

export function useUpsertNutritionDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { date: string; patch: Partial<NutritionDayDto> }) =>
      apiSend<{ day: NutritionDayDto }>(
        'PUT',
        `/apps/health/nutrition/days/${vars.date}`,
        vars.patch,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['health', 'nutrition'] }),
  });
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export interface HealthReportDto {
  id: string;
  type: string;
  title: string;
  periodStart: string | null;
  periodEnd: string | null;
  payload: Record<string, unknown>;
  highlights: string[];
  generatedByAgentId: string | null;
  createdAt: string;
}

export function useHealthReports() {
  return useQuery({
    queryKey: ['health', 'reports'],
    queryFn: () => apiGet<{ reports: HealthReportDto[] }>('/apps/health/reports'),
    staleTime: 60_000,
  });
}

// ─── Data loads ─────────────────────────────────────────────────────────

export interface DataLoadSource {
  id: string;
  label: string;
  description: string;
  accept: string;
  target: string;
}

export interface DataLoadSummary {
  totalRows: number;
  created: number;
  skipped: number;
  errored: number;
  errors: Array<{ row: number; message: string }>;
}

export interface DataLoadRunDto {
  id: string;
  source: string;
  filename: string;
  fileSize: number;
  status: 'completed' | 'failed' | 'partial';
  summary: DataLoadSummary;
  createdAt: string;
}

export interface RunDataLoadResponse {
  loadId: string;
  source: string;
  status: 'completed' | 'failed' | 'partial';
  summary: DataLoadSummary;
}

export function useDataLoadSources() {
  return useQuery({
    queryKey: ['health', 'data-load-sources'],
    queryFn: () => apiGet<{ sources: DataLoadSource[] }>('/apps/health/data-loads/sources'),
    staleTime: 5 * 60_000,
  });
}

export function useDataLoads() {
  return useQuery({
    queryKey: ['health', 'data-loads'],
    queryFn: () => apiGet<{ loads: DataLoadRunDto[] }>('/apps/health/data-loads'),
    staleTime: 30_000,
  });
}

export function useRunDataLoad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { source: string; filename: string; content: string }) =>
      apiSend<RunDataLoadResponse>('POST', '/apps/health/data-loads', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['health', 'data-loads'] });
      // Body-composition feed may have changed; invalidate measurement-ish queries.
      qc.invalidateQueries({ queryKey: ['health', 'measurements'] });
    },
  });
}
